import { Box, List, ListItemText, IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { updateAuditorVisitCheckListItemById } from '../api/updateAuditorVisitCheckListItemById';
import { useAuditorVisitDetailContext } from '../contexts/AuditorVisitDetailContext';
import { CheckListItem } from '../types';
import CheckListFilesModal from './CheckListFilesModal';
import { SqoFile } from '@/common';

const CheckList = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { auditorVisit, invalidateAuditorVisit } = useAuditorVisitDetailContext();
  const [checkList, setCheckList] = useState(auditorVisit.checkList);
  const [filesScreen, setFilesScreen] = useState<'file-list' | 'add-files'>('file-list');
  const [filesModal, setFilesModal] = useState<{
    isOpen: boolean;
    checkListItemId: string;
    files: SqoFile[];
  }>({
    isOpen: false,
    checkListItemId: '',
    files: [],
  });
  const { mutate } = useMutation({
    mutationFn: updateAuditorVisitCheckListItemById,
    onSuccess: () => {
      enqueueSnackbar(
        t('Successfully updated check list item.', {
          defaultValue: 'Successfully updated check list item.',
        }),
        { variant: 'success' }
      );
      invalidateAuditorVisit();
    },
  });

  useEffect(() => {
    setCheckList(auditorVisit.checkList);
  }, [auditorVisit]);

  const handleDateChange = (checkListItem: CheckListItem, event?: dayjs.Dayjs | null) => {
    if (checkList) {
      const newCheckList = checkList.map((item) => {
        if (item.id === checkListItem.id) {
          if (event) {
            return {
              ...item,
              confDat: new Date(event.date(1).toString()).toLocaleDateString('en-CA'),
            };
          }
          return { ...item, confDat: new Date().toLocaleDateString('en-CA') };
        }
        return item;
      });
      setCheckList(newCheckList);
    }
    if (event) {
      mutate({
        checkListItemId: checkListItem.id,
        data: { confDat: event.date.toString() },
      });
    } else {
      mutate({
        checkListItemId: checkListItem.id,
        data: { confDat: new Date().toLocaleDateString('en-CA') },
      });
    }
  };

  return (
    <>
      <CheckListFilesModal
        show={filesModal.isOpen}
        setShow={(isOpen) => setFilesModal((previous) => ({ ...previous, isOpen }))}
        checkListItemId={filesModal.checkListItemId}
        files={filesModal.files}
        setScreen={setFilesScreen}
        screen={filesScreen}
      />
      <List sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 3 }}>
        {!!checkList &&
          checkList.map((checkListItem) => {
            return (
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                key={`check-list-item-${checkListItem.id}`}
                primary={checkListItem.eventTypeName}
                secondary={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={
                          checkListItem.confDat
                            ? dayjs(new Date(checkListItem.confDat).toLocaleDateString('en-CA'))
                            : dayjs('')
                        }
                        onChange={(event) => handleDateChange(checkListItem, event)}
                        sx={{ mt: '1rem', width: '100%' }}
                        label={t('Confirmation date', { defaultValue: 'Confirmation date' })}
                        format="YYYY-MM-DD"
                        disabled
                      />
                    </LocalizationProvider>
                    <IconButton
                      onClick={() =>
                        setFilesModal({
                          isOpen: true,
                          files: checkListItem.files,
                          checkListItemId: checkListItem.id,
                        })
                      }
                    >
                      <SaveIcon />
                    </IconButton>
                  </div>
                }
                sx={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                  alignItems: 'center',
                }}
              />
            );
          })}
      </List>
    </>
  );
};

export default CheckList;
